import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { MapPin, Plus, Trash2, Edit } from "lucide-react";

const TIPOS = [
  { value: "bodega", label: "Bodega / Almacén" },
  { value: "camara", label: "Cámara / Refrigerador" },
  { value: "cocina", label: "Cocina" },
  { value: "salon", label: "Salón" },
  { value: "terraza", label: "Terraza / Exterior" },
  { value: "taller", label: "Taller" },
  { value: "otro", label: "Otro" },
];

const empty = {
  nombre: "", tipo: "bodega", capacidad: "", responsable: "", direccion: "", notas: "", activo: true,
  estado_mantenimiento: "", notas_mantenimiento: "", fecha_ultimo_mantenimiento: "",
  fecha_proximo_mantenimiento: "", responsable_mantenimiento: "",
};

const ESTADOS_MTTO = [
  { value: "bueno", label: "Bueno", color: "text-emerald-700 bg-emerald-100" },
  { value: "regular", label: "Regular", color: "text-amber-700 bg-amber-100" },
  { value: "requiere_atencion", label: "Requiere atención", color: "text-orange-700 bg-orange-100" },
  { value: "en_reparacion", label: "En reparación", color: "text-red-700 bg-red-100" },
];

export default function UbicacionesTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = unidadSlug ? `?unidad_negocio=${unidadSlug}` : "";
      const r = await axios.get(`${API}/ubicaciones${q}`);
      setItems(r.data || []);
    } catch { toast.error("Error al cargar ubicaciones"); }
    finally { setLoading(false); }
  }, [unidadSlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u }); setOpen(true); };

  const handleSave = async () => {
    if (!form.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = { ...form, unidad_negocio: unidadSlug };
      if (editing) {
        await axios.put(`${API}/ubicaciones/${editing.ubicacion_id}`, payload);
        toast.success("Ubicación actualizada");
      } else {
        await axios.post(`${API}/ubicaciones`, payload);
        toast.success("Ubicación creada");
      }
      setOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta ubicación?")) return;
    try {
      await axios.delete(`${API}/ubicaciones/${id}`);
      toast.success("Eliminada");
      fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-4" data-testid={`ubicaciones-${unidadSlug}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Ubicaciones</h2>
          <p className="text-stone-500 text-sm mt-1">Bodegas, cámaras, cocinas y espacios físicos de la unidad.</p>
        </div>
        <Button onClick={openNew} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-nueva-ubicacion">
          <Plus className="w-4 h-4 mr-2" /> Nueva ubicación
        </Button>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm">Cargando...</div>
      ) : items.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-8 text-center text-stone-500">
            <MapPin className="w-10 h-10 mx-auto mb-2 text-stone-300" />
            <p className="text-sm">Sin ubicaciones registradas.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((u) => (
            <Card key={u.ubicacion_id} className="border-stone-200" data-testid={`card-ubi-${u.ubicacion_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-stone-900 truncate">{u.nombre}</p>
                    <p className="text-xs text-stone-500 capitalize">{TIPOS.find(t => t.value === u.tipo)?.label || u.tipo}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`btn-edit-ubi-${u.ubicacion_id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(u.ubicacion_id)} data-testid={`btn-del-ubi-${u.ubicacion_id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                </div>
                {u.capacidad && <p className="text-xs text-stone-600">Capacidad: {u.capacidad}</p>}
                {u.responsable && <p className="text-xs text-stone-600">Responsable: {u.responsable}</p>}
                {u.direccion && <p className="text-xs text-stone-500 truncate">{u.direccion}</p>}
                {u.estado_mantenimiento && (() => {
                  const est = ESTADOS_MTTO.find(e => e.value === u.estado_mantenimiento);
                  return (
                    <div className="mt-2 pt-2 border-t border-stone-100">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${est?.color || "text-stone-700 bg-stone-100"}`}>
                        Mantenimiento: {est?.label || u.estado_mantenimiento}
                      </span>
                      {u.fecha_proximo_mantenimiento && (
                        <p className="text-[10px] text-stone-500 mt-1">Próxima revisión: {u.fecha_proximo_mantenimiento}</p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-ubicacion">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar ubicación" : "Nueva ubicación"}</DialogTitle>
            <DialogDescription className="text-xs">Registra un espacio físico de la unidad.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="ubi-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="ubi-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Capacidad</Label>
                <Input placeholder="ej. 40 personas" value={form.capacidad} onChange={(e) => setForm({ ...form, capacidad: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Responsable</Label>
              <Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>

            {/* ─── MANTENIMIENTO ─── */}
            <div className="pt-3 border-t border-stone-200">
              <p className="text-sm font-semibold text-stone-800 mb-2">Mantenimiento</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={form.estado_mantenimiento || "__none__"} onValueChange={(v) => setForm({ ...form, estado_mantenimiento: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="ubi-estado-mtto"><SelectValue placeholder="Sin registrar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin registrar</SelectItem>
                      {ESTADOS_MTTO.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Responsable</Label>
                  <Input value={form.responsable_mantenimiento || ""} onChange={(e) => setForm({ ...form, responsable_mantenimiento: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Fecha último mant.</Label>
                  <Input type="date" value={form.fecha_ultimo_mantenimiento || ""} onChange={(e) => setForm({ ...form, fecha_ultimo_mantenimiento: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Próxima revisión</Label>
                  <Input type="date" value={form.fecha_proximo_mantenimiento || ""} onChange={(e) => setForm({ ...form, fecha_proximo_mantenimiento: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Notas de mantenimiento</Label>
                  <Textarea rows={2} value={form.notas_mantenimiento || ""} onChange={(e) => setForm({ ...form, notas_mantenimiento: e.target.value })} placeholder="Reparaciones, incidencias, garantías…" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-guardar-ubi">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
